import { IconButton, Stack, TableCell, TableRow, TextField } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoHint from './InfoHint';
import SalaryDataTable from './SalaryDataTable';
import SalaryFormSection from './SalaryFormSection';
import useRequest from '../../hooks/useRequest';
import type { SalaryHoliday } from '../../domain/salary/types';

interface Props {
    holidays: SalaryHoliday[];
    reload: () => Promise<void>;
}

const HolidaysTab = ({ holidays, reload }: Props) => {
    const { t } = useTranslation();
    const request = useRequest();
    const [form, setForm] = useState({ date: '', name: '' });

    const handleAdd = async () => {
        if (!form.date || !form.name) return;
        await request({ path: '/salary/holidays', method: 'POST', params: form });
        setForm({ date: '', name: '' });
        await reload();
    };

    const handleDelete = async (id: number) => {
        await request({ path: `/salary/holidays/${id}`, method: 'DELETE' });
        await reload();
    };

    return (
        <Stack spacing={2}>
            <SalaryFormSection
                title={
                    <>
                        {t('salary.holidays.addTitle')}
                        <InfoHint title={t('salary.info.holidays.section')} />
                    </>
                }
                onAdd={handleAdd}
                addLabel={t('common.add')}
            >
                <TextField
                    size="small"
                    type="date"
                    label={t('salary.holidays.date')}
                    value={form.date}
                    onChange={e => setForm({ ...form, date: e.target.value })}
                    slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                    size="small"
                    label={t('salary.holidays.name')}
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                />
            </SalaryFormSection>

            <SalaryDataTable
                headers={
                    <>
                        <TableCell>{t('salary.holidays.date')}</TableCell>
                        <TableCell>{t('salary.holidays.name')}</TableCell>
                    </>
                }
            >
                {holidays.map(holiday => (
                    <TableRow key={holiday.id}>
                        <TableCell>{holiday.date}</TableCell>
                        <TableCell>{holiday.name}</TableCell>
                        <TableCell align="right">
                            <IconButton size="small" onClick={() => handleDelete(holiday.id)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </TableCell>
                    </TableRow>
                ))}
            </SalaryDataTable>
        </Stack>
    );
};

export default HolidaysTab;
